/**
 * API Request Cache and Deduplication System
 * Prevents multiple simultaneous requests to the same endpoint
 */

class ApiCache {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache timeout
  }

  /**
   * Generate a cache key from request parameters
   */
  generateKey(url, options = {}) {
    const method = options.method || "GET";
    const body = options.body ? JSON.stringify(options.body) : "";
    const key = `${method}:${url}:${body}`;
    console.log("🔑 Generated cache key:", key);
    return key;
  }

  /**
   * Check if a request is already pending
   */
  isPending(key) {
    return this.pendingRequests.has(key);
  }

  /**
   * Get cached response if available and not expired
   */
  getCached(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    if (cached) {
      this.cache.delete(key); // Remove expired cache
    }
    return null;
  }

  /**
   * Set cached response
   */
  setCached(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Add pending request
   */
  addPending(key, promise) {
    this.pendingRequests.set(key, promise);
  }

  /**
   * Remove pending request
   */
  removePending(key) {
    this.pendingRequests.delete(key);
  }

  /**
   * Get or create request with deduplication
   */
  async getOrCreate(key, requestFn, shouldCache = true) {
    console.log("🔍 ApiCache.getOrCreate called with key:", key);
    console.log("🔍 Should cache:", shouldCache);
    console.log("🔍 Pending requests:", this.pendingRequests.size);
    console.log("🔍 Cache size:", this.cache.size);

    // Check if we have a cached response (only if caching is enabled)
    if (shouldCache) {
      const cached = this.getCached(key);
      if (cached) {
        console.log("✅ Returning cached response for key:", key);
        return cached;
      }
    }

    // Check if request is already pending
    if (this.isPending(key)) {
      console.log("⏳ Request already pending for key:", key);
      return await this.pendingRequests.get(key);
    }

    // Create new request
    console.log("🚀 Creating new request for key:", key);
    const promise = requestFn()
      .then((response) => {
        console.log("✅ Request successful for key:", key);
        // Only cache if caching is enabled
        if (shouldCache) {
          this.setCached(key, response);
        }
        this.removePending(key);
        return response;
      })
      .catch((error) => {
        console.log("❌ Request failed for key:", key, error.message);
        this.removePending(key);
        throw error;
      });

    this.addPending(key, promise);
    return await promise;
  }

  /**
   * Clear cache for specific endpoint
   */
  clearCache(url) {
    let clearedCount = 0;
    for (const [key] of this.cache.entries()) {
      if (key.includes(url)) {
        this.cache.delete(key);
        clearedCount++;
      }
    }
    console.log(`🧹 Cleared ${clearedCount} cache entries for: ${url}`);
  }

  /**
   * Clear all cache
   */
  clearAllCache() {
    this.cache.clear();
    this.pendingRequests.clear();
  }
}

// Create singleton instance
const apiCache = new ApiCache();

export default apiCache;
